import * as yup from "yup";
import {
  COACHING_TYPE,
  LIGHTING,
  RECEPTION_CAPACITY_TYPE,
  SURFACE_AREA,
  VENUE_TYPE,
  type StudioEnvironment,
} from "@academy-context/shared/domain/types/studio-environment";

// Shared Yup fragment + mappers for the studio environment sheet (Qualiopi
// indicator 17). Reused by every studio create/update form and the teacher
// onboarding studio subform.
//
// Submission invariants enforced here mirror the backend `ensureSubmittable`:
// at least one coaching typology + certified honor attestation. These apply to
// every owner type (teacher, provider and admin/Opera Off).

export const studioEnvironmentSchema = yup
  .object({
    externalUrl: yup
      .string()
      .transform(value => (value?.trim() ? value.trim() : null))
      .nullable()
      .url("L'URL n'est pas valide")
      .defined(),
    venueType: yup
      .mixed<VENUE_TYPE>()
      .oneOf(Object.values(VENUE_TYPE), "Le type de lieu est requis")
      .required("Le type de lieu est requis"),
    coachingTypes: yup
      .array()
      .of(yup.mixed<COACHING_TYPE>().oneOf(Object.values(COACHING_TYPE)).required())
      .min(1, "Sélectionnez au moins une typologie de coaching")
      .required("Sélectionnez au moins une typologie de coaching"),
    receptionCapacity: yup
      .object({
        type: yup
          .mixed<RECEPTION_CAPACITY_TYPE>()
          .oneOf(Object.values(RECEPTION_CAPACITY_TYPE), "La capacité d'accueil est requise")
          .required("La capacité d'accueil est requise"),
        maxPeople: yup
          .number()
          .nullable()
          .when("type", {
            is: RECEPTION_CAPACITY_TYPE.MASTERCLASS,
            then: schema =>
              schema
                .typeError("Le nombre maximum de personnes est requis")
                .integer("Le nombre maximum de personnes doit être un entier")
                .min(1, "Le nombre maximum de personnes doit être au moins 1")
                .required("Le nombre maximum de personnes est requis"),
            otherwise: schema => schema.transform(() => null).nullable(),
          })
          .defined(),
      })
      .required(),
    materialConditions: yup
      .object({
        surfaceArea: yup
          .mixed<SURFACE_AREA>()
          .oneOf(Object.values(SURFACE_AREA), "La surface est requise")
          .required("La surface est requise"),
        lighting: yup.mixed<LIGHTING>().oneOf(Object.values(LIGHTING), "L'éclairage est requis").required("L'éclairage est requis"),
        hasVentilation: yup.boolean().required(),
        hasHeating: yup.boolean().required(),
        hasWaterAndSanitary: yup.boolean().required(),
        hasSoundproofing: yup.boolean().required(),
      })
      .required(),
    safetyConditions: yup
      .object({
        hasSmokeDetector: yup.boolean().required(),
        hasFireExtinguisher: yup.boolean().required(),
      })
      .required(),
    hasHostingHonorAttestation: yup.boolean().oneOf([true], "L'attestation sur l'honneur est requise").required(),
  })
  .required();

export type StudioEnvironmentFormInputs = yup.InferType<typeof studioEnvironmentSchema>;

// Default form values. `hasHostingHonorAttestation` starts unchecked and
// `coachingTypes` empty so the owner is prompted to complete/certify the sheet.
export const defaultStudioEnvironment: StudioEnvironmentFormInputs = {
  externalUrl: "",
  venueType: VENUE_TYPE.PERSONAL_STUDIO,
  coachingTypes: [],
  receptionCapacity: { type: RECEPTION_CAPACITY_TYPE.INDIVIDUAL, maxPeople: null },
  materialConditions: {
    surfaceArea: SURFACE_AREA.FROM_10_TO_20,
    lighting: LIGHTING.NATURAL,
    hasVentilation: true,
    hasHeating: true,
    hasWaterAndSanitary: true,
    hasSoundproofing: true,
  },
  safetyConditions: { hasSmokeDetector: true, hasFireExtinguisher: true },
  hasHostingHonorAttestation: false,
};

// Form inputs -> API body. Normalizes the optional URL and drops `maxPeople`
// whenever the reception capacity is not a masterclass.
export const formatEnvironmentFormToBody = (environment: StudioEnvironmentFormInputs): StudioEnvironment => ({
  externalUrl: environment.externalUrl?.trim() ? environment.externalUrl.trim() : null,
  venueType: environment.venueType,
  coachingTypes: [...environment.coachingTypes],
  receptionCapacity: {
    type: environment.receptionCapacity.type,
    maxPeople:
      environment.receptionCapacity.type === RECEPTION_CAPACITY_TYPE.MASTERCLASS ? (environment.receptionCapacity.maxPeople ?? null) : null,
  },
  materialConditions: { ...environment.materialConditions },
  safetyConditions: { ...environment.safetyConditions },
  hasHostingHonorAttestation: environment.hasHostingHonorAttestation,
});

// API model -> form inputs (edit prefill). Keeps the URL as a controlled string.
export const formatEnvironmentToFormInputs = (environment: StudioEnvironment): StudioEnvironmentFormInputs => ({
  externalUrl: environment.externalUrl ?? "",
  venueType: environment.venueType,
  coachingTypes: [...environment.coachingTypes],
  receptionCapacity: {
    type: environment.receptionCapacity.type,
    maxPeople: environment.receptionCapacity.maxPeople,
  },
  materialConditions: { ...environment.materialConditions },
  safetyConditions: { ...environment.safetyConditions },
  hasHostingHonorAttestation: environment.hasHostingHonorAttestation,
});
